import type { appRouter, DomainErrorName } from '@faceup/backend/trpc'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import type { TRPCLink } from '@trpc/client'
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { observable } from '@trpc/server/observable'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * For mutations which handle the error themselves (e.g. showing the error next to the input). This prevets showing
       * the error twice.
       */
      skipGlobalErrorHandler?: boolean
    }
  }
}

export type AppRouter = typeof appRouter
export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

export type { DomainErrorName }

/**
 * Reads the backend's domain error discriminator off a caught error.
 *
 * Prefer this over matching `error.message`: the message is human-readable copy that is free
 * to be reworded or localized, while the name is a contract the backend registers explicitly
 * (`trpc/clientFacingErrors.ts`). Because the result is the `DomainErrorName` union, a typo or
 * a backend rename is a compile error rather than a branch that silently stops matching.
 *
 * Returns `undefined` for non-tRPC errors and for backend errors that are not registered as
 * client-facing — both mean "nothing specific to say", so fall back to generic copy.
 */
export const getDomainError = (error: unknown): DomainErrorName | undefined =>
  error instanceof TRPCClientError
    ? (error as TRPCClientError<AppRouter>).data?.domainError
    : undefined

export type TrpcRequestInfo = {
  path: string
  type: 'query' | 'mutation' | 'subscription'
  input: Record<string, unknown>
  response: Record<string, unknown>
  durationMs: number
  isError: boolean
  apiUrl: string
}

type TrpcClientConfig = {
  url: string
  getHeaders: () => Record<string, string>
  onQueryError?: (error: Error) => void
  onMutationError?: (error: Error) => void
  // Called for every request in dev — use to forward to devtools, logging, etc.
  onRequest?: (info: TrpcRequestInfo) => void
}

let errorHandlers: Pick<TrpcClientConfig, 'onQueryError' | 'onMutationError'> = {}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: error => {
      errorHandlers.onQueryError?.(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.skipGlobalErrorHandler) {
        return
      }
      errorHandlers.onMutationError?.(error)
    },
  }),
})

type TrpcProxy = ReturnType<typeof createTRPCOptionsProxy<AppRouter>>

let innerTrpc: TrpcProxy

export const trpc = new Proxy({} as TrpcProxy, {
  get: (_, prop, receiver) => {
    if (!innerTrpc) {
      throw new Error(
        'tRPC client is not initialized.' +
          ' Call configureTrpcClient() and mount TrpcProvider before using trpc.'
      )
    }
    return Reflect.get(innerTrpc, prop, receiver)
  },
})

const createRequestLoggerLink =
  (apiUrl: string, onRequest: NonNullable<TrpcClientConfig['onRequest']>): TRPCLink<AppRouter> =>
  () =>
  ({ op, next }) => {
    const start = Date.now()
    return observable(observer => {
      const sub = next(op).subscribe({
        next(value) {
          const data = (value as unknown as { result?: { data?: unknown } })?.result?.data
          onRequest({
            path: op.path,
            type: op.type,
            input: (op.input as Record<string, unknown>) ?? {},
            response: (data ?? {}) as Record<string, unknown>,
            durationMs: Date.now() - start,
            isError: false,
            apiUrl: `${apiUrl}/${op.path}`,
          })
          observer.next(value)
        },
        error(err) {
          onRequest({
            path: op.path,
            type: op.type,
            input: (op.input as Record<string, unknown>) ?? {},
            response: err as unknown as Record<string, unknown>,
            durationMs: Date.now() - start,
            isError: true,
            apiUrl: `${apiUrl}/${op.path}`,
          })
          observer.error(err)
        },
        complete() {
          observer.complete()
        },
      })
      return () => sub.unsubscribe()
    })
  }

export const configureTrpcClient = (config: TrpcClientConfig) => {
  errorHandlers = config

  const trpcClient = createTRPCClient<AppRouter>({
    links: [
      ...(config.onRequest ? [createRequestLoggerLink(config.url, config.onRequest)] : []),
      httpBatchLink({
        url: config.url,
        headers: () => config.getHeaders(),
        fetch: (url, options) => fetch(url, { ...options, credentials: 'include' }),
      }),
    ],
  })

  innerTrpc = createTRPCOptionsProxy<AppRouter>({
    client: trpcClient,
    queryClient,
  })
}

export {
  QueryClientProvider,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useMutationState,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@tanstack/react-query'
